/**
 * Presentational leaves shared by the two order surfaces.
 *
 * The Orders list panel and views/Order.jsx stay separate containers on
 * purpose — the panel answers "is this the order I meant" at 460px, the page is
 * the record of truth — but the leaves they both draw (money, dates, the status
 * and channel badges) had drifted into two copies. They live here now.
 *
 * Colours come from the shell's CSS variables where the element is inside the
 * page (var(--muted) and friends); anything that can portal to <body> takes a
 * literal hex, because a page-scoped custom property cannot reach it.
 */

import React from 'react';
import dayjs from 'dayjs';
import { OrderStatusEnum, OPEN_STATUSES } from '../../utils/enums/OrderStatusEnum';
import { OrderTypeEnum } from '../../utils/enums/OrderTypeEnum';

export const fmtBDT = (n) => `৳${(Math.round(Number(n) || 0)).toLocaleString('en-US')}`;

export const fmtDate = (d) => (d ? dayjs(d).format('MMM D, YYYY') : '—');

export const fmtDateTime = (d) => (d ? dayjs(d).format('MMM D, YYYY h:mm A') : '—');

/**
 * Compact form for table cells, where two date columns compete for width. The
 * year is dropped for the current year — nearly every row — and shown only when
 * it would otherwise be ambiguous. The full date goes in a title tooltip.
 */
export const fmtDateCell = (d) => {
  if (!d) return '—';
  const x = dayjs(d);
  return x.year() === dayjs().year() ? x.format('D MMM') : x.format('D MMM YY');
};

export const hexToRgba = (hex, a) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return `rgba(148,163,184,${a})`;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${a})`;
};

export const statusMeta = (val) =>
  OrderStatusEnum.find((s) => s.value === val) || { label: 'Unknown', value: val, color: '#94a3b8' };

/** Delivery dates before today on an order that is still open read as late. */
export const isOverdue = (deliveryDate, status) =>
  !!deliveryDate && OPEN_STATUSES.includes(status) && dayjs(deliveryDate).isBefore(dayjs(), 'day');

/**
 * The status badge. `compact` drops the leading dot so a 38px row keeps its
 * line; `onClick` turns it into the trigger for the status menu.
 */
export function StatusBadge({ status, compact = false, interactive = false, onClick, title }) {
  const st = statusMeta(status);
  return (
    <span
      onClick={onClick}
      title={title || st.label}
      style={{
        font: `700 ${compact ? 11 : 11.5}px Outfit, sans-serif`,
        color: st.color,
        background: hexToRgba(st.color, 0.16),
        padding: compact ? '3px 8px' : '4px 8px',
        borderRadius: compact ? 10 : 11,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        minWidth: 0,
        maxWidth: '100%',
        cursor: interactive ? 'pointer' : 'default',
        userSelect: 'none',
      }}
    >
      {!compact && <span style={{ width: 6, height: 6, borderRadius: 3, background: st.color, flexShrink: 0 }} />}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{st.label}</span>
      {interactive && <span style={{ fontSize: 8, opacity: 0.7, flexShrink: 0 }}>▾</span>}
    </span>
  );
}

/**
 * Channel cell. Online orders show the marketplace they came through (the page
 * name is the useful detail); offline ones carry the label alone. `compact`
 * suppresses the marketplace line so the row stays single-height.
 */
export function ChannelBadge({ orderType, marketplace, compact = false }) {
  const isOffline = orderType === OrderTypeEnum.MERCHANT;
  const tint = isOffline ? '#f59e0b' : '#0ea5e9';
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, maxWidth: '100%' }}>
      <span
        style={{
          alignSelf: 'flex-start',
          font: '700 11px Outfit, sans-serif',
          color: tint,
          background: hexToRgba(tint, 0.14),
          padding: '1px 6px',
          borderRadius: 8,
          whiteSpace: 'nowrap',
        }}
      >
        {isOffline ? 'Offline' : 'Online'}
      </span>
      {!compact && !isOffline && marketplace && (
        <span
          title={marketplace}
          style={{
            font: '400 11px Outfit, sans-serif',
            color: 'var(--dim)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {marketplace}
        </span>
      )}
    </span>
  );
}
